import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import WorkCard from '@/components/feature/WorkCard';
import { useSeo } from '@/hooks/useSeo';
import { works } from '@/mocks/catalog';
import { getNewsById, getRelatedNews, latestNews } from '@/mocks/news';

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = id ? getNewsById(id) : undefined;

  useSeo({
    title: item ? `${item.title} - 91大神影视平台` : '资讯不存在 - 91大神影视平台',
    description: item ? item.summary.slice(0, 120) : '该资讯暂时不可用，返回资讯频道浏览最新的影视新闻与影评。',
    keywords: item ? `91大神, ${item.category}, 影视资讯, ${item.title.slice(0, 12)}` : '91大神, 影视资讯',
    canonicalPath: `/news/${id || ''}`,
    image: item?.cover,
    type: 'article',
  });

  if (!item) {
    return (
      <SiteLayout>
        <div className="mx-auto flex w-full max-w-[1400px] flex-col items-center justify-center px-4 py-24 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-article-line text-2xl"></i>
          </span>
          <h1 className="mt-5 font-display text-xl font-bold text-foreground-950">这篇资讯暂时不可用</h1>
          <p className="mt-2 max-w-md text-sm text-foreground-500">链接可能已失效，你可以返回资讯频道继续阅读。</p>
          <Link
            to="/news"
            className="mt-6 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-bold text-foreground-950 transition-colors hover:bg-accent-400"
          >
            返回资讯频道
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const related = getRelatedNews(item, 3);
  const relatedPool = related.length > 0 ? related : latestNews.filter((n) => n.id !== item.id).slice(0, 3);
  const pickWorks = works.filter((w) => w.rating >= 8.8).slice(0, 5);

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-8">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <article className="min-w-0">
            <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
              <Link to="/" className="transition-colors hover:text-accent-700">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-300"></i>
              <Link to="/news" className="transition-colors hover:text-accent-700">
                影视资讯
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-300"></i>
              <span className="truncate text-foreground-800">{item.category}</span>
            </nav>

            <header className="mt-4 rounded-lg border border-background-200/70 bg-background-50 p-5 md:p-7">
              <span className="inline-flex rounded-md bg-accent-100 px-2.5 py-1 text-[11px] font-medium text-accent-800">
                {item.category}
              </span>
              <h1 className="mt-3 font-display text-2xl font-extrabold leading-snug text-foreground-950 md:text-3xl">
                {item.title}
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-foreground-500">{item.summary}</p>
              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-background-200/70 pt-4 text-xs text-foreground-400">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-3-line"></i>
                  {item.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {item.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  阅读约 {Math.max(3, Math.round(item.body.join('').length / 260))} 分钟
                </span>
                <Link to="/news" className="ml-auto flex items-center gap-1 whitespace-nowrap text-foreground-600 hover:text-accent-700">
                  更多资讯 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </header>

            <div className="mt-4 overflow-hidden rounded-lg border border-background-200/70 bg-background-50">
              <div className="h-[220px] w-full overflow-hidden bg-background-200 md:h-[360px]">
                <img
                  src={item.cover}
                  alt={item.title}
                  title={`${item.title} - 影视资讯配图`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="p-5 md:p-7">
                {item.body.map((paragraph, index) => (
                  <p
                    key={index}
                    className={`text-sm leading-8 text-foreground-700 ${index === 0 ? 'first-letter:font-display first-letter:text-2xl first-letter:font-bold first-letter:text-foreground-950' : 'mt-4'}`}
                  >
                    {paragraph}
                  </p>
                ))}
                <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-md bg-background-100 px-4 py-3">
                  <p className="text-xs text-foreground-500">
                    <i className="ri-information-line mr-1 text-accent-600"></i>
                    本文为平台原创内容，欢迎通过「关于我们」页面反馈建议。
                  </p>
                  <Link to="/news" className="whitespace-nowrap text-xs text-foreground-700 hover:text-accent-700">
                    ← 返回资讯列表
                  </Link>
                </div>
              </div>
            </div>

            <section className="mt-6">
              <h2 className="font-display text-lg font-bold text-foreground-950">相关文章</h2>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                {relatedPool.map((news) => (
                  <Link
                    key={news.id}
                    to={`/news/${news.id}`}
                    className="group overflow-hidden rounded-lg border border-background-200/70 bg-background-50 transition-colors hover:border-accent-300"
                  >
                    <div className="h-[110px] w-full overflow-hidden bg-background-200">
                      <img
                        src={news.cover}
                        alt={news.title}
                        title={`${news.title} - 相关文章`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-3">
                      <p className="text-[11px] text-accent-700">{news.category}</p>
                      <h3 className="mt-1 line-clamp-2 text-sm font-semibold leading-snug text-foreground-950 group-hover:text-accent-700">
                        {news.title}
                      </h3>
                      <p className="mt-1.5 text-[11px] text-foreground-400">{news.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          </article>

          <aside className="flex flex-col gap-4">
            <section className="rounded-lg border border-background-200/70 bg-background-50 p-4">
              <h2 className="font-display text-sm font-bold text-foreground-950">最新资讯</h2>
              <ul className="mt-3 space-y-3">
                {latestNews
                  .filter((n) => n.id !== item.id)
                  .map((news) => (
                    <li key={news.id}>
                      <Link to={`/news/${news.id}`} className="group flex gap-2.5">
                        <div className="h-14 w-20 shrink-0 overflow-hidden rounded-md bg-background-200">
                          <img
                            src={news.cover}
                            alt={news.title}
                            title={`${news.title} - 最新资讯`}
                            className="h-full w-full object-cover object-top"
                          />
                        </div>
                        <div className="min-w-0">
                          <h3 className="line-clamp-2 text-xs font-medium leading-snug text-foreground-800 group-hover:text-accent-700">
                            {news.title}
                          </h3>
                          <p className="mt-1 text-[11px] text-foreground-400">{news.date}</p>
                        </div>
                      </Link>
                    </li>
                  ))}
              </ul>
              <Link
                to="/news"
                className="mt-4 flex items-center justify-between rounded-md bg-primary-950 px-3 py-2 text-xs text-background-50 transition-colors hover:bg-primary-800"
              >
                全部影视资讯 <i className="ri-arrow-right-line"></i>
              </Link>
            </section>

            <section className="rounded-lg border border-background-200/70 bg-background-100 p-4">
              <h2 className="font-display text-sm font-bold text-foreground-950">文中提到的作品</h2>
              <div className="mt-3 grid grid-cols-2 gap-3">
                {pickWorks.slice(0, 4).map((work) => (
                  <WorkCard key={work.id} work={work} />
                ))}
              </div>
            </section>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}